import React from 'react';

import { ButtonBase, Typography } from '@mui/material';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Avatar from 'src/components/ui/Avatar';
import Link from 'src/components/ui/Link';

import { colours } from 'src/constants/colours';
import { breakpoints } from 'src/constants/breakpoints';
import stopPropagation from 'src/utils/stopPropagation';
import formatDistanceToNow from 'src/utils/formatDistanceToNow';

import getOwnerLink from 'src/utils/getOwnerLink';
import useOwnerSubDisplayName from 'src/hooks/useOwnerSubDisplayName';
import type { Medium, Owner } from 'src/types/__generated__/graphql';
import { styledComponent } from 'src/utils/styled';

const StyledTitle = styledComponent(Typography)<{ $isInChatPreviewCard?: boolean }>`
    color: ${colours.black.main};
    ${({ $isInChatPreviewCard }) =>
        $isInChatPreviewCard &&
        `color: ${colours.primary.main};
        font-size: 0.75rem;
        `}
`;

const StyledMeta = styledComponent(Typography)<{ $isInChatPreviewCard?: boolean }>`
    color: ${colours.black.O60};
    ${({ $isInChatPreviewCard }) =>
        $isInChatPreviewCard &&
        `
        font-size: 0.5rem;
        max-width: 7rem;
    `}
`;

const StyledBox = styledComponent(Box)`
    margin-right: 1rem;
    @media screen and (max-width: ${breakpoints.tabletPortrait - 1}px) {
        margin-right: 0rem;
    }
`;

interface Props {
    owner: Owner;
    createdAt: string;
    comment?: boolean;
    feedAd?: boolean;
    isInChatPreviewCard?: boolean;
    disableRedirectionOnUserProfile?: boolean;
}

const Author: React.FC<Props> = ({
    owner,
    createdAt,
    comment = false,
    feedAd = false,
    isInChatPreviewCard,
    disableRedirectionOnUserProfile,
}) => {
    const { i18n } = useTranslation(['common', 'profile']);

    const subDisplayName = useOwnerSubDisplayName(owner);
    const ownerLink = getOwnerLink(owner as Owner);

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!owner || !owner.id) {
        return null;
    }

    const isCompany = Boolean(owner.company?.name);

    return (
        <Box display="inline-flex" alignItems="center" width="100%">
            <ButtonBase
                sx={{ width: '100%' }}
                LinkComponent={Link}
                href={ownerLink}
                disabled={disableRedirectionOnUserProfile}
                onClick={stopPropagation}
            >
                <Avatar
                    data-target="author-avatar"
                    data-target-id={`author-avatar-id-${owner.id}`}
                    image={owner.image as Medium}
                    alt={owner.displayName}
                    fallbackText={owner.initials}
                    isCompany={isCompany}
                    size={40}
                />
                <Box display="flex" flexDirection="column" ml="0.5rem" width="calc(100% - 3rem)">
                    {comment ? (
                        <>
                            <Box display="flex" flexDirection="row" alignItems="center">
                                <Typography
                                    variant="h6"
                                    style={{ color: colours.black.main }}
                                    data-target="author-name"
                                    data-target-id={`author-id-${owner.id}`}
                                    noWrap
                                >
                                    {owner.displayName}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    style={{ color: colours.black.main, marginLeft: '0.188rem' }}
                                    noWrap
                                >
                                    {' · '}
                                    {formatDistanceToNow(createdAt, i18n.language as SupportedLangs, true)}
                                </Typography>
                            </Box>
                            <Box display="flex" width="12rem">
                                <Typography
                                    variant="caption"
                                    style={{ color: colours.black.O60, lineHeight: 1 }}
                                    noWrap
                                >
                                    {subDisplayName}
                                </Typography>
                            </Box>
                        </>
                    ) : null}
                    {!comment && !feedAd ? (
                        <>
                            <StyledTitle
                                variant="h5"
                                data-target="author-name"
                                data-target-id={`author-id-${owner.id}`}
                                $isInChatPreviewCard={isInChatPreviewCard as boolean}
                                noWrap
                            >
                                {owner.displayName}
                            </StyledTitle>

                            <Box display="flex" flexDirection="row">
                                <StyledMeta
                                    variant="caption"
                                    $isInChatPreviewCard={isInChatPreviewCard as boolean}
                                    noWrap
                                >
                                    {subDisplayName}
                                </StyledMeta>

                                <StyledBox ml="0.1rem" display="flex" alignItems="center">
                                    <StyledMeta
                                        variant="caption"
                                        $isInChatPreviewCard={isInChatPreviewCard as boolean}
                                        noWrap
                                    >
                                        · {formatDistanceToNow(createdAt, i18n.language as SupportedLangs, true)}
                                    </StyledMeta>
                                </StyledBox>
                            </Box>
                        </>
                    ) : null}
                    {feedAd ? (
                        <Box
                            display="flex"
                            flexDirection="row"
                            justifyContent="space-between"
                            alignItems="center"
                            width="100%"
                        >
                            <Box flex="1" display="flex" flexDirection="column">
                                <Typography
                                    variant="h5"
                                    style={{ color: colours.primary.main }}
                                    data-target="author-name"
                                    data-target-id={`author-id-${owner.id}`}
                                    noWrap
                                >
                                    {owner.displayName}
                                </Typography>
                                <Typography variant="caption" style={{ color: colours.black.O38 }} noWrap>
                                    {subDisplayName}
                                </Typography>
                            </Box>
                            <Typography variant="caption" style={{ color: colours.black.O38 }}>
                                {formatDistanceToNow(createdAt, i18n.language as SupportedLangs, true)}
                            </Typography>
                        </Box>
                    ) : null}
                </Box>
            </ButtonBase>
        </Box>
    );
};

export default Author;
